import { useEffect, useState } from "react";
import { Link, Accordion, AccordionSummary, AccordionDetails, Typography, Box } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CircularProgress from "@mui/material/CircularProgress";
import { fetchSummaryStatus } from "../services/api";

// Expanding Item
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

function ServiceItem({ name, status, messages, lastChecked }) {
    const color =
        {
            resolved: "success",
            issue_detected: "error",
        }[status.toLowerCase().replace(/ /g, "_")] || "default";

    const StatusIcon = status.toLowerCase().replace(/ /g, "_") === "resolved" ? CheckCircleIcon : ErrorIcon;
    const Title = status.toLowerCase().replace(/ /g, "_") === "resolved" ? "Resolved" : "Disruptions";

    return (
        <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%">
                    <Box display="block" sx={{ flexGrow: 1 }}>
                        <Typography className="fw-bold">
                            {Title}: {name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {!!lastChecked ? new Date(lastChecked).toDateString() : "-"}
                        </Typography>
                    </Box>
                    <StatusIcon color={color} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        {status}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {messages.map((message, i, m) => {
                    return (
                        <Box key={i} display="block">
                            <Typography variant="caption" className="fw-bold">
                                {message.title}
                            </Typography>
                            <div className="d-block"></div>
                            <Typography variant="caption">{message.description}</Typography>
                            {i + 1 === m.length ? "" : <hr />}
                        </Box>
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
}

function ServicesStatusHistoryPage() {
    const now = new Date().toDateString();

    const [prev, setPrev] = useState("");
    let [data, setData] = useState([]);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        fetchSummaryStatus()
            .then((data) => {
                setFetching(false);
                if (!!data?.length) {
                    const filter = data.sort((a, b) => (a?.name > b?.name ? 1 : a?.name < b?.name ? -1 : 0)).filter((d) => d.history.some((h) => h.has_error));
                    setData(filter);
                    let startDate = filter.reduce((prev, cur) => {
                        const c = cur.history.map((h) => new Date(h.date));
                        prev.push(...c);
                        return prev;
                    }, []);
                    startDate = updatePrevDate(startDate);
                    setPrev(startDate);
                }
            })
            .catch((err) => {
                console.error(err);
                setFetching(false);
            });
    }, []);

    function updatePrevDate(dates) {
        if (!dates) return "";
        return dates
            .sort((a, b) => (new Date(a) < new Date(b) ? -1 : new Date(a) > new Date(b) ? 1 : 0))
            .at(0)
            .toDateString();
    }

    return (
        <div>
            <div className="PageBanner py-2 shadow-sm">
                <img src="/botnoi-logo-text.svg" alt="Botnoi Logo With Text" />
            </div>
            <div className="container">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Link href="/" underline="none">
                        Home
                    </Link>
                    <KeyboardArrowRightIcon />
                    <Typography>Service Status</Typography>
                </div>
                <h1 className="text-center">Service Status History</h1>
                <div className="d-flex justify-content-center">
                    <Typography variant="caption" color="text.secondary">
                        {prev} - {now}
                    </Typography>
                </div>

                <div className="mb-5"></div>

                <div className="row">
                    <div className="col-12 col-md-10 offset-md-1 col-lg-6 offset-lg-3">
                        {fetching || !data?.length ? (
                            <div
                                className="d-flex align-items-center justify-content-center flex-column bg-white rounded-3 shadow-sm"
                                style={{ minHeight: "25rem" }}
                            >
                                {!data.length ? (
                                    <>
                                        <p className="fw-bold">No Know Issues</p>
                                        <Typography variant="caption">There have been no resolved</Typography>
                                        <Typography variant="caption">issues in the last seven days.</Typography>
                                    </>
                                ) : (
                                    <div className="d-flex align-items-center gap-3">
                                        <CircularProgress disableShrink size={16} />
                                        <span>Loading...</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                {data.map((d) => (
                                    <ServiceItem
                                        key={`${d.name.toLowerCase().replace(/ /g, "-")}`}
                                        name={d.name}
                                        status={d.latest.success ? "Resolved" : "Issue detected"}
                                        messages={d.history
                                            .filter((h) => h.has_error)
                                            .reduce((prev, cur) => {
                                                prev.push(...cur.events);
                                                return prev;
                                            }, [])
                                            .sort((a, b) =>
                                                new Date(a.checked_at) > new Date(b.checked_at) ? -1 : new Date(a.checked_at) < new Date(b.checked_at) ? 1 : 0
                                            )
                                            .map((h) => ({
                                                title: `${new Date(h.checked_at)}: ${!h.error ? "Resolved" : "Disruptions"}`,
                                                description: h.message,
                                            }))}
                                        lastChecked={d.latest.checked_at}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ServicesStatusHistoryPage;
